import type { Ref } from 'vue';
import { computed } from 'vue';

import type { ITranslationsKeyMap } from '@modules/i18n/types';

export default function getTranslationKeys(): Ref<ITranslationsKeyMap> {
  return computed(() => ({
    common: {
      reports: {
        configuration: {
          header: 'REPORTS.HEADER',
          generate_report: 'REPORTS.GENERATE_REPORT',
          download_report: 'REPORTS.DOWNLOAD_REPORT',
          some_error_occured: 'REPORTS.SOME_ERROR_OCCURRED',
          refresh_page_button: 'REPORTS.REFRESH_PAGE_BUTTON',
          date_range: {
            today: 'REPORTS.DATE_RANGE.today',
            yesterday: 'REPORTS.DATE_RANGE.yesterday',
            last_7_days: 'REPORTS.DATE_RANGE.last_7_days',
            week: 'REPORTS.DATE_RANGE.week',
            last_week: 'REPORTS.DATE_RANGE.last_week',
            month: 'REPORTS.DATE_RANGE.month',
            last_month: 'REPORTS.DATE_RANGE.last_month',
            year: 'REPORTS.DATE_RANGE.year',
          },
          group_by: {
            date: {
              group_by_day: 'REPORTS.GROUP_BY.group_by_day',
              group_by_week: 'REPORTS.GROUP_BY.group_by_week',
              group_by_month: 'REPORTS.GROUP_BY.group_by_month',
              group_by_alltime: 'REPORTS.GROUP_BY.group_by_alltime',
            },
            affiliate_params: {
              label: 'REPORTS.GROUP_BY.AFFILIATE.LABEL',
              add_grouping_btn: 'REPORTS.GROUP_BY.AFFILIATE.ADD_GROUPING_BTN',
              group_by_merchant: 'REPORTS.GROUP_BY.group_by_merchant',
              group_by_affiliate: 'REPORTS.GROUP_BY.group_by_affiliate',
              group_by_tracker: 'REPORTS.GROUP_BY.group_by_tracker',
              group_by_anid: 'REPORTS.GROUP_BY.group_by_anid',
              group_by_anid1: 'REPORTS.GROUP_BY.group_by_anid1',
              group_by_anid2: 'REPORTS.GROUP_BY.group_by_anid2',
              group_by_anid3: 'REPORTS.GROUP_BY.group_by_anid3',
              group_by_anid4: 'REPORTS.GROUP_BY.group_by_anid4',
              group_by_anid5: 'REPORTS.GROUP_BY.group_by_anid5',
              group_by_player: 'REPORTS.GROUP_BY.group_by_player',
              group_by_manager: 'REPORTS.GROUP_BY.group_by_manager',
              group_by_country: 'REPORTS.GROUP_BY.group_by_country',
              group_by_tracker_group: 'REPORTS.GROUP_BY.group_by_tracker_group',
              group_by_department: 'REPORTS.GROUP_BY.group_by_department',
              group_by_tracker_country: 'REPORTS.GROUP_BY.group_by_tracker_country',
              group_by_fixed_payment_info: 'REPORTS.GROUP_BY.group_by_fixed_payment_info',
              group_by_condition: 'REPORTS.GROUP_BY.group_by_condition',
              group_by_traffic_source: 'REPORTS.GROUP_BY.group_by_traffic_source',
            },
            player_params: {
              all_players: 'REPORTS.GROUP_BY.all_players',
              new_players: 'REPORTS.GROUP_BY.new_players',
              new_ftds: 'REPORTS.GROUP_BY.new_ftds',
              old_players: 'REPORTS.GROUP_BY.old_players',
            },
            per_page: {
              rows_per_page: 'REPORTS.GROUP_BY.rows_per_page',
            },
          },
          segments: {
            all_players: 'REPORTS.SEGMENTS_ALL_PLAYERS',
            no_name: 'REPORTS.SEGMENTS_NO_NAME',
            add_new_element: 'REPORTS.SEGMENTS_ADD_NEW_ELEMENT',
            tabs: {
              merchant_ids: 'REPORTS.SEGMENTS_TABS.merchant_ids',
              managers: 'REPORTS.SEGMENTS_TABS.managers',
              departments: 'REPORTS.SEGMENTS_TABS.departments',
              affiliates: 'REPORTS.SEGMENTS_TABS.affiliates',
              tracker_country: 'REPORTS.SEGMENTS_TABS.tracker_country',
              tracker_traffic_source: 'REPORTS.SEGMENTS_TABS.tracker_traffic_source',
              trackers: 'REPORTS.SEGMENTS_TABS.trackers',
              anid: 'REPORTS.SEGMENTS_TABS.anid',
              anid1: 'REPORTS.SEGMENTS_TABS.anid1',
              anid2: 'REPORTS.SEGMENTS_TABS.anid2',
              anid3: 'REPORTS.SEGMENTS_TABS.anid3',
              anid4: 'REPORTS.SEGMENTS_TABS.anid4',
              anid5: 'REPORTS.SEGMENTS_TABS.anid5',
              creatives: 'REPORTS.SEGMENTS_TABS.creatives',
              players: 'REPORTS.SEGMENTS_TABS.players',
              stats: 'REPORTS.SEGMENTS_TABS.stats',
              date_of_registration: 'REPORTS.SEGMENTS_TABS.date_of_registration',
              date_of_ftd: 'REPORTS.SEGMENTS_TABS.date_of_ftd',
              date_of_last_deposit: 'REPORTS.SEGMENTS_TABS.date_of_last_deposit',
              date_of_last_rmp_activity: 'REPORTS.SEGMENTS_TABS.date_of_last_rmp_activity',
              inactive_days: 'REPORTS.SEGMENTS_TABS.inactive_days',
              first_deposit_size: 'REPORTS.SEGMENTS_TABS.first_deposit_size',
              last_deposit_size: 'REPORTS.SEGMENTS_TABS.last_deposit_size',
              amount_of_deposits: 'REPORTS.SEGMENTS_TABS.amount_of_deposits',
              average_deposit_size: 'REPORTS.SEGMENTS_TABS.average_deposit_size',
              sum_of_deposits: 'REPORTS.SEGMENTS_TABS.sum_of_deposits',
              ngr_all_time: 'REPORTS.SEGMENTS_TABS.ngr_all_time',
              ggr_all_time: 'REPORTS.SEGMENTS_TABS.ggr_all_time',
              ngr_this_month: 'REPORTS.SEGMENTS_TABS.ngr_this_month',
              ggr_this_month: 'REPORTS.SEGMENTS_TABS.ggr_this_month',
              country_code: 'REPORTS.SEGMENTS_TABS.country_code',
              name: 'REPORTS.SEGMENTS_TABS_NAME',
            },
          },
          structures: {
            all_columns: 'REPORTS.STRUCTURES_ALL_COLUMNS',
            no_name: 'REPORTS.STRUCTURES_NO_NAME',
            add_new_element: 'REPORTS.STRUCTURES_ADD_NEW_ELEMENT',
            info_message_for_readonly_structure: 'REPORTS.STRUCTURES_READONLY_MESSAGE',
            tabs: {
              general_columns: 'REPORTS.STRUCTURES_TABS.general_columns',
              player_columns: 'REPORTS.STRUCTURES_TABS.player_columns',
              product_columns: 'REPORTS.STRUCTURES_TABS.product_columns',
              comission_columns: 'REPORTS.STRUCTURES_TABS.comission_columns',
              stats_columns: 'REPORTS.STRUCTURES_TABS.stats_columns',
              name: 'REPORTS.STRUCTURES_TABS_NAME',
              aasm_state: 'REPORTS.STRUCTURES_TABS.aasm_state',
              for_private: 'REPORTS.STRUCTURES_TABS.for_private',
              for_admin: 'REPORTS.STRUCTURES_TABS.for_admin',
              for_affiliate: 'REPORTS.STRUCTURES_TABS.for_affiliate',
              for_all: 'REPORTS.STRUCTURES_TABS.for_all',
            },
          },
        },
        dialog_window: {
          remove: {
            title: 'REPORTS.DIALOG_TITLE',
            question: 'REPORTS.DIALOG_QUESTION',
            yes: 'REPORTS.DIALOG_YES',
            no: 'REPORTS.DIALOG_NO',
          },
        },
        result: {
          header: 'REPORTS.RESULT_HEADER',
          subtotal: 'REPORTS.SUB_TOTAL_ENTRIES',
          subtotal_enable: 'REPORTS.SUB_TOTAL_ENABLE',
          subtotal_disable: 'REPORTS.SUB_TOTAL_DISABLE',
          total: 'REPORTS.TOTAL_ENTRIES',
        },
        table_actions: {
          change_player: 'REPORTS.TABLE_ACTIONS.CHANGE_PLAYER',
        },
      },
    },
  }));
}
